import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";

import { environment } from "../../../../environments/environment";
import { buildRegionProxyHeaders } from "../../utils/region-proxy";
import { MetricsRequest, MetricsResponse } from "./metrics.model";

@Injectable({
  providedIn: "root",
})
export class MetricsService {
  private http = inject(HttpClient);

  private baseUrl = environment.developmentUrl;

  getMetrics(request: MetricsRequest): Observable<MetricsResponse[]> {
    // Always call the local API. When a non-local region is selected the
    // X-DU-Proxy-Origin header routes the request through the back-end
    // cross-region proxy.
    const headers = buildRegionProxyHeaders(request.regionUrl);

    // If productIds are provided, use the multi-product endpoint
    if (request.productIds && request.productIds.length > 0) {
      const url = `${this.baseUrl}metrics/multi?startTime=${encodeURIComponent(request.startTime)}&endTime=${encodeURIComponent(request.endTime)}&binBy=${request.binBy}`;

      const requestBody: any = {
        productIds: request.productIds,
      };

      // Add request types if provided
      if (request.requestTypes && request.requestTypes.length > 0) {
        requestBody.requestTypes = request.requestTypes;
      }

      return this.http.post<MetricsResponse[]>(url, requestBody, { headers });
    } else {
      // Use the original endpoint for single product
      let url = `${this.baseUrl}metrics?startTime=${encodeURIComponent(request.startTime)}&endTime=${encodeURIComponent(request.endTime)}&binBy=${request.binBy}`;

      // Add request types as query parameters if provided
      if (request.requestTypes && request.requestTypes.length > 0) {
        const requestTypeParams = request.requestTypes
          .map(rt => `requestTypes=${encodeURIComponent(rt)}`)
          .join("&");
        url = `${url}&${requestTypeParams}`;
      }

      return this.http.get<MetricsResponse[]>(url, { headers });
    }
  }

  getDateRangeFromTimeRange(timeRange: string): {
    startTime: string;
    endTime: string;
  } {
    const now = new Date();
    const startDate = new Date();

    const endTime = now.toISOString();

    switch (timeRange) {
      case "Last 2 days":
        startDate.setDate(now.getDate() - 2);
        break;
      case "Last 3 days":
        startDate.setDate(now.getDate() - 3);
        break;
      case "Last 7 days":
        startDate.setDate(now.getDate() - 7);
        break;
      case "Last 30 days":
        startDate.setDate(now.getDate() - 30);
        break;
      case "Last 90 days":
        startDate.setDate(now.getDate() - 90);
        break;
      case "Last 180 days":
        startDate.setDate(now.getDate() - 180);
        break;
      case "Last 360 days":
        startDate.setDate(now.getDate() - 360);
        break;
      default:
        startDate.setDate(now.getDate() - 30);
        break;
    }

    const startTime = startDate.toISOString();

    return { startTime, endTime };
  }
}
